import React, { useState, useEffect, useCallback } from 'react';
import { FileDown, Search, Filter, Package, AlertCircle } from 'lucide-react';
import PageIdentifier from '../shared/PageIdentifier';
import SCREEN_IDS from '../../utils/screenIds';
import * as XLSX from 'xlsx';
import { getGoldPurityFactor, stoneWeightToGrams } from '../../utils/calculations';
import { todayCentral } from '../../shared/utils/dateUtils';

// Live cost value calculation: gold + stone cost_price + charges (no stale DB columns)
const calcItemCostValue = (item) => {
  const netWeight = parseFloat(item.net_weight || item.gross_weight || 0);
  const goldRate = parseFloat(item.gold_rate || 0);
  const goldPurity = parseInt(item.gold_purity || 18);
  const goldCost = netWeight * goldRate * getGoldPurityFactor(goldPurity);
  const stones = item.stones || [];
  const stoneCost = stones.reduce((s, stone) => s + stoneWeightToGrams(stone.weight, stone.unit) * parseFloat(stone.cost_price || 0), 0);
  const wastage = goldCost * (parseFloat(item.wastage_percentage || 0) / 100);
  const making = netWeight * parseFloat(item.making_charges || 0);
  const labor = netWeight * parseFloat(item.labor_charges || 0);
  const other = parseFloat(item.other_costs || 0);
  return parseFloat((goldCost + stoneCost + wastage + making + labor + other).toFixed(2));
};

const VendorStock = () => {
  // State
  const [vendors, setVendors] = useState([]);
  const [vendorDetails, setVendorDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    vendor: 'all',
    status: 'all',
    search: '',
    sortBy: 'name'
  });
  
  const [expandedVendor, setExpandedVendor] = useState(null);

  const fetchVendorsAndStock = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('=== FETCHING VENDOR STOCK REPORT ===');
      
      // Fetch vendors list
      const vendorsRes = await fetch('/api/vendors', {
        credentials: 'include'
      });
      
      if (!vendorsRes.ok) {
        throw new Error(`Failed to fetch vendors: ${vendorsRes.statusText}`);
      }
      
      const vendorsData = await vendorsRes.json();
      console.log('Vendors API Response:', vendorsData);
      
      let vendorsList = Array.isArray(vendorsData) ? vendorsData : vendorsData.data || [];
      
      // Apply filters
      if (filters.vendor !== 'all') {
        vendorsList = vendorsList.filter(v => v.id === parseInt(filters.vendor));
      }
      
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        vendorsList = vendorsList.filter(v => 
          v.name.toLowerCase().includes(searchLower) ||
          v.contact_person?.toLowerCase().includes(searchLower)
        );
      }
      
      // Sort vendors
      vendorsList.sort((a, b) => {
        switch (filters.sortBy) {
          case 'name':
            return a.name.localeCompare(b.name);
          case 'contact':
            return (a.contact_person || '').localeCompare(b.contact_person || '');
          case 'recent':
            return new Date(b.created_at) - new Date(a.created_at);
          default:
            return 0;
        }
      });
      
      setVendors(vendorsList);
      
      // Fetch purchase order details and inventory for each vendor
      const detailsMap = {};
      for (const vendor of vendorsList) {
        try {
          // Fetch purchase orders
          const poRes = await fetch(`/api/purchase-orders?vendor_id=${vendor.id}`, {
            credentials: 'include'
          });
          
          let poList = [];
          if (poRes.ok) {
            const poData = await poRes.json();
            console.log(`PO API response for vendor ${vendor.id}:`, poData);
            
            if (poData.success && Array.isArray(poData.data)) {
              poList = poData.data;
            } else if (Array.isArray(poData)) {
              poList = poData;
            } else if (poData.data && Array.isArray(poData.data)) {
              poList = poData.data;
            }
          }
          
          // Fetch inventory items for this vendor
          const invRes = await fetch(`/api/jewelry?vendor_id=${vendor.id}&page=1&limit=10000`, {
            credentials: 'include'
          });
          
          let invItems = [];
          if (invRes.ok) {
            const invData = await invRes.json();
            console.log(`Inventory API response for vendor ${vendor.id}:`, invData);
            
            // Handle different response formats
            if (invData.success && Array.isArray(invData.data)) {
              invItems = invData.data;
              console.log(`  Raw data length: ${invData.data.length}`);
            } else if (Array.isArray(invData)) {
              invItems = invData;
              console.log(`  Direct array length: ${invData.length}`);
            } else if (invData.data && Array.isArray(invData.data)) {
              invItems = invData.data;
              console.log(`  Wrapped data length: ${invData.data.length}`);
            }
            
            // Also check pagination.total if available
            if (invData.pagination) {
              console.log(`  Pagination total: ${invData.pagination.total}, actual data: ${invItems.length}`);
            }
          }
          
          console.log(`Vendor ${vendor.name}:`);
          console.log(`  - Inventory Items: ${invItems.length} (type: ${typeof invItems}, isArray: ${Array.isArray(invItems)})`);
          console.log(`  - Purchase Orders: ${poList.length} (type: ${typeof poList}, isArray: ${Array.isArray(poList)})`);
          
          // Validate inventory items is actually an array
          const validInvItems = Array.isArray(invItems) ? invItems : [];
          const validPoList = Array.isArray(poList) ? poList : [];
          
          console.log(`  - Valid Inventory Items: ${validInvItems.length}`);
          console.log(`  - Valid Purchase Orders: ${validPoList.length}`);
          
          // Calculate vendor metrics
          const totalOrders = validPoList.length;
          const pendingOrders = validPoList.filter(po => po.status === 'pending').length;
          const completedOrders = validPoList.filter(po => po.status === 'received').length;
          const totalValue = validPoList.reduce((sum, po) => sum + (parseFloat(po.total_amount) || 0), 0);
          
          // Calculate inventory metrics
          const totalInventoryItems = validInvItems.length;
          const inventoryValue = validInvItems.reduce((sum, item) => sum + calcItemCostValue(item), 0);
          
          detailsMap[vendor.id] = {
            totalOrders,
            pendingOrders,
            completedOrders,
            totalValue,
            orders: validPoList,
            totalInventoryItems,
            inventoryValue,
            inventoryItems: validInvItems
          };
        } catch (err) {
          console.warn(`Error fetching data for vendor ${vendor.id}:`, err);
          detailsMap[vendor.id] = {
            totalOrders: 0,
            pendingOrders: 0,
            completedOrders: 0,
            totalValue: 0,
            orders: [],
            totalInventoryItems: 0,
            inventoryValue: 0,
            inventoryItems: []
          };
        }
      }
      
      // Log final summary
      console.log('=== FINAL VENDOR DETAILS MAP ===');
      Object.entries(detailsMap).forEach(([vendorId, details]) => {
        console.log(`Vendor ${vendorId}:`);
        console.log(`  totalInventoryItems: ${details.totalInventoryItems}`);
        console.log(`  totalOrders: ${details.totalOrders}`);
        console.log(`  inventoryItems array length: ${details.inventoryItems?.length || 0}`);
        console.log(`  orders array length: ${details.orders?.length || 0}`);
      });
      
      setVendorDetails(detailsMap);
      console.log('=== VENDOR STOCK REPORT FETCH COMPLETE ===');
    } catch (err) {
      console.error('Error fetching vendor stock:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Fetch vendors and their stock details
  useEffect(() => {
    fetchVendorsAndStock();
  }, [fetchVendorsAndStock]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const exportToExcel = () => {
    try {
      // Prepare export data
      const exportData = vendors.map(vendor => ({
        'Vendor Code': vendor.code || vendor.name,
        'Contact Person': vendor.contact_person || '-',
        'Email': vendor.email || '-',
        'Phone': vendor.phone || '-',
        'Inventory Items': vendorDetails[vendor.id]?.totalInventoryItems || 0,
        'Inventory Value (₹)': (vendorDetails[vendor.id]?.inventoryValue || 0).toFixed(2),
        'Total Orders': vendorDetails[vendor.id]?.totalOrders || 0,
        'Pending Orders': vendorDetails[vendor.id]?.pendingOrders || 0,
        'Completed Orders': vendorDetails[vendor.id]?.completedOrders || 0,
        'PO Total Value (₹)': (vendorDetails[vendor.id]?.totalValue || 0).toFixed(2),
        'Location': vendor.city || '-'
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Vendor Stock');
      
      // Format columns
      ws['!cols'] = [
        { wch: 20 }, // Vendor Name
        { wch: 18 }, // Contact Person
        { wch: 20 }, // Email
        { wch: 15 }, // Phone
        { wch: 12 }, // Total Orders
        { wch: 12 }, // Pending Orders
        { wch: 12 }, // Completed Orders
        { wch: 15 }, // Total Value
        { wch: 15 }  // Location
      ];

      XLSX.writeFile(wb, `vendor-stock-${todayCentral()}.xlsx`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Failed to export data');
    }
  };

  const toggleVendorExpand = (vendorId) => {
    setExpandedVendor(expandedVendor === vendorId ? null : vendorId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading vendor stock report...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageIdentifier screenId={SCREEN_IDS.REPORTS.VENDOR_REPORT} />
      
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Package className="w-6 h-6 text-amber-600" />
                Vendor Stock Report
              </h1>
              <p className="text-gray-600 mt-1">Track vendor orders and performance</p>
            </div>
            <button
              onClick={exportToExcel}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
            >
              <FileDown className="w-4 h-4" />
              Export Excel
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Search */}
            <div className="flex-1 min-w-xs">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Search className="w-4 h-4 inline mr-2" />
                Search Vendors
              </label>
              <input
                type="text"
                placeholder="Vendor name, contact..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500"
              />
            </div>

            {/* Sort */}
            <div className="min-w-xs">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Filter className="w-4 h-4 inline mr-2" />
                Sort By
              </label>
              <select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500"
              >
                <option value="name">Vendor Name</option>
                <option value="contact">Contact Person</option>
                <option value="recent">Recently Added</option>
              </select>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-red-900">Error</h3>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Vendor List */}
        {vendors.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">No vendors found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {vendors.map(vendor => {
              const details = vendorDetails[vendor.id] || {};
              const isExpanded = expandedVendor === vendor.id;

              return (
                <div key={vendor.id} className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition">
                  {/* Vendor Summary */}
                  <button
                    onClick={() => toggleVendorExpand(vendor.id)}
                    className="w-full p-4 text-left hover:bg-gray-50 transition flex items-center justify-between"
                  >
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{vendor.code || vendor.name}</h3>
                      <p className="text-sm text-gray-600">{vendor.contact_person || 'No contact'} • {vendor.email || 'No email'}</p>
                    </div>
                    <div className="flex items-center gap-4 mr-4 text-xs">
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-900">{details.totalInventoryItems || 0}</div>
                        <div className="text-xs text-gray-500">In Stock</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-blue-600">₹{((details.inventoryValue || 0) / 100000).toFixed(1)}L</div>
                        <div className="text-xs text-gray-500">Inv Value</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-900">{details.totalOrders || 0}</div>
                        <div className="text-xs text-gray-500">POs</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-amber-600">{details.pendingOrders || 0}</div>
                        <div className="text-xs text-gray-500">Pending</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-green-600">{details.completedOrders || 0}</div>
                        <div className="text-xs text-gray-500">Done</div>
                      </div>
                    </div>
                  </button>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="border-t border-gray-200 p-4 bg-gray-50">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">Contact Information</h4>
                          <div className="space-y-1 text-sm text-gray-600">
                            <p><strong>Person:</strong> {vendor.contact_person || '-'}</p>
                            <p><strong>Email:</strong> {vendor.email || '-'}</p>
                            <p><strong>Phone:</strong> {vendor.phone || '-'}</p>
                            <p><strong>Location:</strong> {vendor.city || '-'}</p>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">Order Statistics</h4>
                          <div className="space-y-1 text-sm">
                            <p><strong className="text-gray-900">Total Orders:</strong> <span className="text-gray-600">{details.totalOrders || 0}</span></p>
                            <p><strong className="text-gray-900">Pending:</strong> <span className="text-amber-600">{details.pendingOrders || 0}</span></p>
                            <p><strong className="text-gray-900">Completed:</strong> <span className="text-green-600">{details.completedOrders || 0}</span></p>
                            <p><strong className="text-gray-900">Total Value:</strong> <span className="text-gray-600">₹{(details.totalValue || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span></p>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">Inventory Statistics</h4>
                          <div className="space-y-1 text-sm">
                            <p><strong className="text-gray-900">Total Items:</strong> <span className="text-gray-600">{details.totalInventoryItems || 0}</span></p>
                            <p><strong className="text-gray-900">Inventory Value:</strong> <span className="text-blue-600">₹{(details.inventoryValue || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span></p>
                            <p><strong className="text-gray-900">Avg Item Value:</strong> <span className="text-gray-600">{details.totalInventoryItems > 0 ? `₹${((details.inventoryValue || 0) / details.totalInventoryItems).toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : '₹0'}</span></p>
                          </div>
                        </div>
                      </div>

                      {/* Recent Orders */}
                      {details.orders && details.orders.length > 0 && (
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">Recent Orders</h4>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-white">
                                <tr className="border-b border-gray-200">
                                  <th className="text-left py-2 px-2 text-gray-700 font-medium">PO Number</th>
                                  <th className="text-left py-2 px-2 text-gray-700 font-medium">Date</th>
                                  <th className="text-left py-2 px-2 text-gray-700 font-medium">Amount</th>
                                  <th className="text-left py-2 px-2 text-gray-700 font-medium">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {details.orders.slice(0, 5).map(order => (
                                  <tr key={order.id} className="border-b border-gray-100 hover:bg-white">
                                    <td className="py-2 px-2 text-gray-900">PO-{order.id.toString().padStart(4, '0')}</td>
                                    <td className="py-2 px-2 text-gray-600">{new Date(order.created_at).toLocaleDateString()}</td>
                                    <td className="py-2 px-2 text-gray-900">₹{(parseFloat(order.total_amount) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                                    <td className="py-2 px-2">
                                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                                        order.status === 'received' ? 'bg-green-100 text-green-800' :
                                        order.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                                        'bg-gray-100 text-gray-800'
                                      }`}>
                                        {order.status || 'Unknown'}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {details.orders.length > 5 && (
                              <p className="text-xs text-gray-500 mt-2">+{details.orders.length - 5} more orders</p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Recent Inventory Items */}
                      {details.inventoryItems && details.inventoryItems.length > 0 && (
                        <div className="mt-4">
                          <h4 className="font-medium text-gray-900 mb-2">Recent Inventory Items ({details.totalInventoryItems} total)</h4>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-white">
                                <tr className="border-b border-gray-200">
                                  <th className="text-left py-2 px-2 text-gray-700 font-medium">Item Code</th>
                                  <th className="text-left py-2 px-2 text-gray-700 font-medium">Description</th>
                                  <th className="text-left py-2 px-2 text-gray-700 font-medium">Status</th>
                                  <th className="text-left py-2 px-2 text-gray-700 font-medium">Price (₹)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {details.inventoryItems.slice(0, 5).map(item => (
                                  <tr key={item.id} className="border-b border-gray-100 hover:bg-white">
                                    <td className="py-2 px-2 text-gray-900 font-medium">{item.code}</td>
                                    <td className="py-2 px-2 text-gray-600">{item.name || item.description || 'N/A'}</td>
                                    <td className="py-2 px-2">
                                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                                        item.status === 'In Stock' ? 'bg-green-100 text-green-800' :
                                        item.status === 'Sold' ? 'bg-gray-100 text-gray-800' :
                                        'bg-amber-100 text-amber-800'
                                      }`}>
                                        {item.status || 'Unknown'}
                                      </span>
                                    </td>
                                    <td className="py-2 px-2 text-gray-900">₹{calcItemCostValue(item).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {details.inventoryItems.length > 5 && (
                              <p className="text-xs text-gray-500 mt-2">+{details.inventoryItems.length - 5} more items</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorStock;
